import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';

import { useDisclosure } from '@chakra-ui/hooks';

import { deletePickups, fetchPickupById, updatePickup } from 'api/pickup';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useCollectionPoints } from 'hooks/useCollectionPoints';

import { successToast } from 'utils/successToast';

import { useAppSelector } from 'store/hooks';
import { locationSelector } from 'store/slices/washhub';

import { Pickup } from 'types/pickup';
import { RefType, EditPickupModalProps } from './EditPickupModal.props';

import EditPickupModalView from './EditPickupModal.view';
import { toApiPayloadDateFormat } from 'utils/dateUtils';
import { useSites } from 'hooks';

const Container = forwardRef<RefType, EditPickupModalProps>((props, ref) => {
  const { renderNextPickupButton, renderPreviousPickupButton } = props;

  const queryClient = useQueryClient();

  const selectedWashHub = useAppSelector(locationSelector);
  const washhubId = selectedWashHub?._id || '';

  const [pickup, setPickup] = useState<Pickup | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const { isOpen, onOpen, onClose: _onClose } = useDisclosure();
  const { data = [], isLoading } = useCollectionPoints();
  const { data: sites } = useSites();

  const onClose = () => {
    _onClose();

    setIsEditing(false);
  };

  useImperativeHandle(
    ref,
    () => ({
      onOpen: (_pickup) => {
        setPickup(_pickup);

        onOpen();
      },
    }),
    [onOpen],
  );

  const onMtnSuccess = (message: string) => () => {
    successToast(message);

    // Invalidate the specific pickup query
    queryClient.invalidateQueries([`fetch-pickups-ById`, pickup?._id]);

    // Invalidate list queries
    queryClient.invalidateQueries(`fetch-pickups-${washhubId}`);

    queryClient.invalidateQueries(`fetch-pickups-status-count-${washhubId}`);

    queryClient.invalidateQueries(`fetch-orders`);

    onClose();
  };

  const updateMtn = useMutation(updatePickup, {
    onSuccess: onMtnSuccess('Pickup Updated'),
  });

  const deleteMtn = useMutation(deletePickups, {
    onSuccess: onMtnSuccess('Pickup Deleted'),
  });
  const queryKey = [`fetch-pickups-ById`, pickup?._id];

  const queryFn = () =>
    fetchPickupById({
      id: pickup?._id || '',
    });
  const options = {
    enabled: !!pickup?._id,
    keepPreviousData: false, // Don't keep previous data to allow updates to show immediately
    staleTime: 0, // Always refetch on invalidation
    cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  };
  const {
    data: pickupData,
    isLoading: pickupDataLoading,
    isFetching,
  } = useQuery(queryKey, queryFn, options);

  const _props = {
    isOpen,
    pickup,
    onClose,
    isUpdating: updateMtn.isLoading,
    isEditing,
    setIsEditing,
    collectionPoints: data,
    sites,
    isLoadingPoints: isLoading,
    isLoadingPickup: pickupDataLoading,
    isDeleting: deleteMtn.isLoading,
    renderNextPickupButton,
    renderPreviousPickupButton,
    onUpdate: async (payload: any) =>
      updateMtn.mutate({
        ...payload,
        _id: pickup?._id,
        date: toApiPayloadDateFormat(payload.date),
      }),
    onDelete: () => {
      if (!pickup?._id) {
        return;
      }

      deleteMtn.mutate([pickup._id]);
    },
  };

  return <EditPickupModalView {..._props} pickupToShow={pickupData} />;
});

const EditPickupModal = memo(Container);

export default EditPickupModal;
